export const DEFAULT_RESULT_FORM = {
  resultats: "",
  classement: "",
  observations: "",
  podium_url: "",
  tableau_url: "",
};

export const RESULT_TOKENS = [
  {
    value: "✅",
    label: "Victoire",
    shortLabel: "Gagné",
    className: "bg-emerald-600 text-white hover:bg-emerald-700",
  },
  {
    value: "❌",
    label: "Défaite",
    shortLabel: "Perdu",
    className: "bg-red-600 text-white hover:bg-red-700",
  },
  {
    value: "N",
    label: "Nul",
    shortLabel: "Nul",
    className: "bg-slate-700 text-white hover:bg-slate-900",
  },
  {
    value: "F",
    label: "Forfait",
    shortLabel: "Forfait",
    className: "bg-orange-600 text-white hover:bg-orange-700",
  },
  {
    value: "D",
    label: "Disqualifié",
    shortLabel: "Disq.",
    className: "bg-purple-600 text-white hover:bg-purple-700",
  },
];

export const RESULT_RANK_OPTIONS = [
  "1èr(e)",
  "2ème",
  "3ème",
  "4ème",
  "5ème",
  "7ème",
  "9ème",
  "N.C.",
];

export function getUserId(user) {
  return user?.userId || user?.id || null;
}

export function getAdherentFullName(adherent) {
  return (
    [adherent?.prenom, adherent?.nom].filter(Boolean).join(" ") || "Judoka"
  );
}

export function formatResultDate(value) {
  if (!value) return "Date non renseignée";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "Date non renseignée";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function formatShortDate(value) {
  if (!value) return "Non renseignée";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "Non renseignée";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

export function buildResultForm(event) {
  const adherentEvent = event?.AdherentEvent || {};

  return {
    resultats: adherentEvent.resultats || "",
    classement: adherentEvent.classement || "",
    observations: adherentEvent.observations || "",
    podium_url: adherentEvent.podium_url || "",
    tableau_url: adherentEvent.tableau_url || "",
  };
}

export function buildResultPayload(result, form) {
  const adherentEvent = result?.event?.AdherentEvent || {};

  return {
    adherent_id: adherentEvent.adherent_id || result?.adherent?.id,
    event_id: adherentEvent.event_id || result?.event?.id,
    resultats: form.resultats || "",
    classement: form.classement || "",
    observations: form.observations || "",
    podium_url: form.podium_url || "",
    tableau_url: form.tableau_url || "",
  };
}

export function getResultKey(result) {
  return `${result?.adherent?.id || "adherent"}-${result?.event?.id || "event"}`;
}

export function resultHasData(result) {
  const adherentEvent = result?.event?.AdherentEvent || {};

  return Boolean(
    adherentEvent.resultats ||
      adherentEvent.classement ||
      adherentEvent.observations ||
      adherentEvent.podium_url ||
      adherentEvent.tableau_url,
  );
}

export function parseResultTokens(value = "") {
  const tokens = String(value || "").split("");

  return tokens.reduce(
    (acc, token) => {
      if (token === "✅") acc.wins += 1;
      else if (token === "❌") acc.losses += 1;
      else if (token === "N") acc.draws += 1;
      else if (token === "F") acc.forfaits += 1;
      else if (token === "D") acc.disqualified += 1;

      return acc;
    },
    {
      wins: 0,
      losses: 0,
      draws: 0,
      forfaits: 0,
      disqualified: 0,
    },
  );
}

export function getStatusLabel(status) {
  if (status === "accepted") return "Accepté";
  if (status === "pending") return "En attente";
  if (status === "rejected") return "Refusé";

  return "Statut inconnu";
}

export function getStatusClass(status) {
  if (status === "accepted") {
    return "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200";
  }

  if (status === "rejected") {
    return "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-200";
  }

  return "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200";
}

export function getUniqueOptions(items = [], getValue, getLabel = getValue) {
  const map = new Map();

  items.forEach((item) => {
    const value = getValue(item);

    if (!value) return;

    if (!map.has(value)) {
      map.set(value, getLabel(item));
    }
  });

  return Array.from(map.entries()).map(([value, label]) => ({
    value,
    label,
  }));
}

export function buildResultsStats(results = []) {
  const global = results.reduce(
    (acc, result) => {
      const adherentEvent = result?.event?.AdherentEvent || {};
      const parsed = parseResultTokens(adherentEvent.resultats);

      acc.wins += parsed.wins;
      acc.losses += parsed.losses;
      acc.draws += parsed.draws;
      acc.totalFights +=
        parsed.wins +
        parsed.losses +
        parsed.draws +
        parsed.forfaits +
        parsed.disqualified;

      if (adherentEvent.classement) acc.ranked += 1;
      if (resultHasData(result)) acc.withResults += 1;
      else acc.withoutResults += 1;

      return acc;
    },
    {
      totalResults: results.length,
      withResults: 0,
      withoutResults: 0,
      wins: 0,
      losses: 0,
      draws: 0,
      ranked: 0,
      totalFights: 0,
    },
  );

  return global;
}

export function sortResultsByDateDesc(results = []) {
  return [...results].sort((a, b) => {
    const dateA = new Date(a?.event?.start || 0).getTime();
    const dateB = new Date(b?.event?.start || 0).getTime();

    return dateB - dateA;
  });
}